import { useCallback, useEffect, useRef, useState } from 'react';
import { buildChallengeUrl, maxGuessesFor } from '@lexicon/core';
import { Toast } from '../components/Toast';
import type { ToastMessage } from '../game/useGame';
import { getChallengeResults, type ChallengeResult } from '../lib/api';
import { loadMyChallenges, type MyChallenge } from '../lib/myChallenges';
import { shareOrigin } from '../lib/platform';
import { navigate } from '../lib/router';
import { shareResult } from '../lib/share';
import { useApp, useStrings } from '../state/AppContext';

const TOAST_MS = 2000;



type ResultsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; results: ChallengeResult[] };

function ResultsList({ state, rows, onRetry }: { state: ResultsState; rows: number; onRetry: () => void }) {
  const t = useStrings();
  if (state.status === 'loading') return <p className="text-sm text-dim">{t.myChallenges.loadingResults}</p>;
  if (state.status === 'error') {
    return (
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-dim">{t.myChallenges.resultsFailed}</p>
        <button
          type="button"
          onClick={onRetry}
          className="min-h-[44px] rounded-lg border border-hairline px-3 text-xs font-semibold"
        >
          {t.myChallenges.retry}
        </button>
      </div>
    );
  }
  if (state.results.length === 0) return <p className="text-sm text-dim">{t.myChallenges.noPlayers}</p>;
  return (
    <ul className="flex flex-col gap-1 text-sm">
      {state.results.map((r, i) => (
        <li key={i}>
          {r.won
            ? t.myChallenges.solvedIn(r.playerName ?? t.myChallenges.anonymous, r.guessCount, rows)
            : t.myChallenges.didNotSolve(r.playerName ?? t.myChallenges.anonymous)}
        </li>
      ))}
    </ul>
  );
}

function ChallengeCard({ challenge, deviceId, onToast }: {
  challenge: MyChallenge;
  deviceId: string;
  onToast: (text: string) => void;
}) {
  const { code, word, createdAt } = challenge;
  const { language, t } = useApp();
  const [state, setState] = useState<ResultsState>({ status: 'loading' });

  // Shared by the mount-time fetch and any later Retry click, so a Retry
  // fired just before unmount (card removed, screen navigated away) still
  // gets cancelled instead of setting state on an unmounted card.
  const cancelledRef = useRef(false);

  const fetchResults = useCallback(() => {
    setState({ status: 'loading' });
    void (async () => {
      const res = await getChallengeResults(code, deviceId);
      if (cancelledRef.current) return;
      setState(res.ok ? { status: 'ready', results: res.value } : { status: 'error' });
    })();
  }, [code, deviceId]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchResults();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchResults]);

  const url = buildChallengeUrl(shareOrigin(), code, word);

  const handleShare = useCallback(() => {
    void (async () => {
      const outcome = await shareResult(t.challenge.invite(url));
      if (outcome === 'copied') onToast(t.common.copied);
      if (outcome === 'failed') onToast(t.common.shareFailed);
    })();
  }, [url, onToast, t]);

  return (
    <article aria-label={t.myChallenges.label(code)} className="glass rounded-2xl p-4">
      <p className="text-base font-bold tracking-[0.3em]">{word.toUpperCase()}</p>
      <p className="text-xs text-dim">
        {t.myChallenges.meta(
          code,
          new Intl.DateTimeFormat(language, { month: 'short', day: 'numeric' }).format(new Date(createdAt)),
        )}
      </p>
      <div className="mt-2">
        {/* The challenge's own word says how many rows its board had (spec §10.1). */}
        <ResultsList state={state} rows={maxGuessesFor(word)} onRetry={fetchResults} />
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={handleShare}
          className="min-h-[44px] flex-1 rounded-xl bg-grad-accent text-sm font-semibold text-white"
        >
          {t.myChallenges.shareLink}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/c/${code}`)}
          className="min-h-[44px] flex-1 rounded-xl border border-hairline text-sm font-semibold"
        >
          {t.myChallenges.open}
        </button>
      </div>
    </article>
  );
}

export function MyChallengesScreen() {
  const { storage, deviceId, t } = useApp();
  const [challenges, setChallenges] = useState<MyChallenge[] | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const toastSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await loadMyChallenges(storage);
      if (!cancelled) setChallenges(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [storage]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const showToast = useCallback((text: string) => {
    toastSeq.current += 1;
    setToast({ id: toastSeq.current, text });
  }, []);

  return (
    <section className="flex flex-1 flex-col gap-3">
      {/* I3: /challenges is a dead end without this — there is no browser
          chrome to fall back on inside the Capacitor shell. */}
      <button
        type="button"
        className="glass min-h-[44px] self-start rounded-xl px-4 text-sm font-semibold"
        onClick={() => navigate('/')}
      >
        {t.common.toToday}
      </button>

      <h2 className="text-lg font-semibold">{t.myChallenges.title}</h2>

      {challenges === null ? (
        <p className="p-6 text-center text-dim">{t.common.loading}</p>
      ) : null}

      {challenges && challenges.length === 0 ? (
        <>
          <p className="text-sm text-dim">{t.myChallenges.none}</p>
          <button
            type="button"
            className="min-h-[44px] rounded-xl bg-grad-accent px-5 text-sm font-semibold text-white"
            onClick={() => navigate('/challenge/new')}
          >
            {t.myChallenges.create}
          </button>
        </>
      ) : null}

      {challenges?.map((challenge) => (
        <ChallengeCard
          key={challenge.code}
          challenge={challenge}
          deviceId={deviceId}
          onToast={showToast}
        />
      ))}

      <Toast toast={toast} />
    </section>
  );
}
